import {Intervals} from "../../Intervals"

/**
 * Occupancy Report settings schema.
 *
 * Occupancy has NO manual-run settings: the only configuration is the recurring
 * delivery schedule (interval + weekday), which is template-only. On a manual
 * run the schema renders nothing, so `hasManualRunSettings` is false and the
 * report uses the shared run-and-go panel (no Report Settings drawer trigger).
 * `interval` lives on the template row (fromContext) and `weekday` mirrors the
 * pre-schema OccupancyReportSettings.vue `receiveWeekday`; setting keys are
 * unchanged so saved templates are unaffected.
 *
 * Unlike most reports, Occupancy's backend (OccupancyReportController@create)
 * only supports Daily and Weekly delivery, so the interval field uses this
 * restricted list rather than the shared Intervals.INTERVAL_OPTIONS. Keys match
 * Intervals.DAILY / Intervals.WEEKLY so the value set is unchanged from the
 * pre-schema OccupancyReportSettings.vue.
 */
const OCCUPANCY_INTERVAL_OPTIONS = {
    [Intervals.DAILY]: 'Daily',
    [Intervals.WEEKLY]: 'Weekly',
}
export default {
    slug: 'occupancy-report',
    icon:
        '<path stroke="none" d="M0 0h24v24H0z" fill="none"/>'
        + '<path d="M10 13a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />'
        + '<path d="M8 21v-1a2 2 0 0 1 2 -2h4a2 2 0 0 1 2 2v1" />'
        + '<path d="M15 5a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />'
        + '<path d="M17 10h2a2 2 0 0 1 2 2v1" />'
        + '<path d="M5 5a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />'
        + '<path d="M3 13v-1a2 2 0 0 1 2 -2h2" />',
    permission: 'manage_occupancy_report',
    title: 'Occupancy',
    description: 'How many people were in each room over the period.',
    info: 'Hourly people counts for each zone with occupancy sensors, charted with one line per day. Each hour shows the highest count reported that hour, with a zone\'s sensors added together. An Excel file of the hourly numbers is attached to the email.',

    sections: [
        {
            // Template-only: the recurring send schedule. On-demand runs pick the
            // window in the run form, so there is nothing to configure here.
            title: 'Scheduled Delivery',
            description: 'Automatically send this report on a recurring schedule.',
            showIf: ({isSavable}) => isSavable === true,
            fields: [
                {
                    key: 'interval',
                    type: 'select',
                    label: 'Send frequency',
                    description: 'How often the report is emailed, around 1 AM site time. Daily covers the previous day, and weekly the 7 days before.',
                    options: OCCUPANCY_INTERVAL_OPTIONS,
                    fromContext: true,
                },
                {
                    key: 'weekday',
                    type: 'select',
                    label: 'Day to receive report',
                    description: 'The day the weekly report is emailed, around 1 AM site time. It covers the 7 days ending the day before.',
                    options: Intervals.WEEKDAY_OPTIONS,
                    showIf: ({interval}) => interval === Intervals.WEEKLY,
                },
            ],
        },
    ],
}
